/* eslint-disable @typescript-eslint/no-deprecated */

import React from 'react'
import styled, { useTheme } from 'styled-components'
import { Collapse, IconButton } from '@mui/material'
import { CancelOutlined, HelpOutlineOutlined } from '@mui/icons-material'
import { Button, ArrowLeftIcon } from '@tumelo/designsystem'
import { Heading, Body } from '../../buildingBlocks/Typography'
import { Breakpoint } from '../../styles'

interface Props {
  title?: string | React.ReactElement
  helpText?: string | React.ReactNode
  callToAction?: React.ReactElement
  showBack?: boolean
  onClickGoBack?: () => void
  iconOnlyAltText?: string
  className?: string // internal for styled components https://styled-components.com/docs/basics#styling-any-component
}

/**
 * PageHeader - Should only be 1 per page as it contains a h1
 */
export const PageHeader: React.FC<Props> = ({
  title,
  helpText,
  callToAction,
  showBack = false,
  onClickGoBack,
  iconOnlyAltText,
  className,
}) => {
  const [isOpen, setOpen] = React.useState(false)
  const { colors } = useTheme()
  return (
    <Row className={className}>
      {showBack && (
        <Column style={{ flex: 0 }}>
          <Button
            iconOnly={<ArrowLeftIcon />}
            variant="tertiary"
            iconOnlyAltText={iconOnlyAltText}
            onClick={onClickGoBack}
            className={className}
          />
        </Column>
      )}
      {title && (
        <Column style={{ flex: 2 }}>
          <SubTitleContainer>
            <StyledHeading mt="0" as="h1">
              {title}
              {helpText && (
                <StyledIconButton
                  title="Toggle help"
                  style={inheritFontSize}
                  disableRipple
                  onClick={() => setOpen(!isOpen)}
                >
                  {isOpen ? <CancelOutlined fontSize="inherit" /> : <HelpOutlineOutlined fontSize="inherit" />}
                </StyledIconButton>
              )}
            </StyledHeading>
          </SubTitleContainer>
        </Column>
      )}
      {callToAction && <Column style={{ flex: 1, order: 1 }}>{callToAction}</Column>}
      {helpText && (
        <HelpColumn>
          <Collapse in={isOpen}>
            <Body as="p" color={colors.grey.dark}>
              {helpText}
            </Body>
          </Collapse>
        </HelpColumn>
      )}
    </Row>
  )
}

const inheritFontSize = { fontSize: 'inherit' }

const StyledIconButton = styled(IconButton)`
  color: ${({ theme }) => theme.colors.primary.medium};
`

const StyledHeading = styled(Heading)`
  display: flex;
  align-items: center;
  margin-bottom: 0;
`

const HelpColumn = styled.div`
  order: 1;
  flex-basis: 100%;
  flex-grow: 1;
  @media (max-width: ${Breakpoint.tablet}) {
    order: 0;
  }
`

const Row = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 3rem;
  @media (max-width: ${Breakpoint.tablet}) {
    margin-bottom: 1rem;
  }
`

const SubTitleContainer = styled.div`
  display: flex;
  align-items: baseline;
`

const Column = styled.div`
  &:first-child {
    margin-right: 2rem;
  }
`
